import { capitalizeFirst, stripHtmlForMetaDescription } from '.';
import { getSeoMeta } from '../src/translations/seo';

const enPostTypes = {
  cityoffer: {
    meta_title: 'Ron’s City Offers – Local Deals, Ideas & Announcements',
    meta_description:
      'Discover what’s new around you! From local promotions and new openings to creative offers and business announcements by the community.',
  },
  housing: {
    meta_title: 'Ron’s Housing Market – Find or Offer Rooms & Flats',
    meta_description:
      'Looking for a place or have one to offer? Browse or post shared flats, apartments, and short-term stays from the community.',
  },
  jobs: {
    meta_title: 'Ron’s Job Market – Discover or Post Local Opportunities',
    meta_description:
      'Explore job listings shared by the community! Find full-time roles, freelance gigs, internships, or post your own job offer.',
  },
  events: {
    meta_title: 'Market Events – Discover & Share What’s Happening',
    meta_description:
      'Stay updated with the latest events! Explore concerts, exhibitions, markets, meetups and more — or post your own and join the buzz.',
  },
  secondhand: {
    meta_title: 'Ron’s Secondhand Market – Buy, Sell & Reuse',
    meta_description:
      'Discover secondhand gems from the community! Sell what you no longer need, find pre-loved items, or give things away sustainably.',
  },
};
const frPostTypes = {
  cityoffer: {
    meta_title: 'Offres locales de Ron – Promotions, nouveautés & idées',
    meta_description:
      'Découvre les offres du moment ! Bons plans locaux, ouvertures récentes, promotions et idées partagées par la communauté.',
  },
  housing: {
    meta_title:
      'Logement sur Ron – Cherche ou propose un appartement ou une chambre',
    meta_description:
      'Trouve ton prochain logement ou propose le tien ! Colocations, appartements ou séjours de courte durée à partager dans la communauté.',
  },
  jobs: {
    meta_title: 'Marché de l’emploi de Ron – Trouve ou publie une offre',
    meta_description:
      'Consulte les offres d’emploi de la communauté ! Jobs, freelances, stages – ou poste ton annonce pour trouver la bonne personne.',
  },
  events: {
    meta_title:
      'Événements – Découvre et partage ce qui se passe autour de toi',
    meta_description:
      'Ne rate rien des événements à venir ! Concerts, expos, marchés, rencontres – découvre ou publie ton propre événement facilement.',
  },
  secondhand: {
    meta_title: 'Marché seconde main de Ron – Vendre, acheter & réutiliser',
    meta_description:
      'Explore les bonnes affaires d’occasion ! Vends ce dont tu n’as plus besoin, trouve des objets utiles ou donne-les à d’autres.',
  },
};
const dePostTypes = {
  cityoffer: {
    meta_title: 'Ron’s Stadtangebote – Lokale Deals, Tipps & Neuigkeiten',
    meta_description:
      'Entdecke aktuelle Angebote aus deiner Umgebung! Von Aktionen und Neueröffnungen bis zu kreativen Ideen und Ankündigungen aus der Community.',
  },
  housing: {
    meta_title: 'Ron’s Wohnungsmarkt – WG-Zimmer, Wohnungen & Untermiete',
    meta_description:
      'Suchst du ein Zimmer oder eine Wohnung? Entdecke oder teile Wohnangebote wie WGs, Mietwohnungen oder Zwischenmieten.',
  },
  jobs: {
    meta_title: 'Ron’s Jobmarkt – Finde oder poste Stellenangebote',
    meta_description:
      'Entdecke Jobs aus der Community! Ob Festanstellung, Nebenjob oder Praktikum – hier kannst du suchen oder dein Angebot teilen.',
  },
  events: {
    meta_title: 'Veranstaltungskalender – Entdecken & selber Events teilen',
    meta_description:
      'Finde heraus, was läuft! Konzerte, Ausstellungen, Märkte und mehr – oder veröffentliche deinen eigenen Event und mach mit.',
  },
  secondhand: {
    meta_title: 'Ron’s Secondhand-Marktplatz – Kaufen, verkaufen & weitergeben',
    meta_description:
      'Finde tolle Secondhand-Angebote oder biete selbst etwas an! Nachhaltig stöbern, verkaufen oder verschenken in der Community.',
  },
};    

function getMetaDescriptionForPosts(data) {
  return stripHtmlForMetaDescription(data);
}

const defaultImagesAndTypes = (img = '') => {
  return {
    type: 'website',
    images: [
      {
        url:
          img ||
          'https://www.ronorp.net/templates/default/images/neu/i-love-ron.jpg',
        width: 800,
        height: 600,
        alt: 'Og Image',
      },
    ],
  };
};

const twitterMeta = {
  handle: '@handle',
  site: '@site',
  cardType: 'summary_large_image',
};

export const DEFAULT_META = (city) => {
  return {
    title: 'Ron Orp is Switzerland’s urban community platform.',
    description:
      'Ron Orp is Switzerland’s urban community platform—connecting locals across cities. Discover daily tips on culture, events, dining, housing, and more.',
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const COMMUNITY_HOME_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.community_home_meta_title,
    description: seo?.community_home_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_ZURICH_EN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_zurich_en_meta_title,
    description: seo?.ask_zurich_en_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/2?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_ZURICH_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_zurich_meta_title,
    description: seo?.ask_zurich_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/3?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_ROMANDIE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_romandie_meta_title,
    description: seo?.ask_romandie_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/4?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_BASEL_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_basel_meta_title,
    description: seo?.ask_basel_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/6?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_BERN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_bern_meta_title,
    description: seo?.ask_bern_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/7?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_LUZERN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_luzern_meta_title,
    description: seo?.ask_luzern_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/8?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_ST_GALLEN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_st_gallen_meta_title,
    description: seo?.ask_st_gallen_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/9?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_WINTERTHUR_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_winterthur_meta_title,
    description: seo?.ask_winterthur_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/10?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const FAMILY_ZURICH_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.zurie_family_meta_title,
    description: seo?.zurie_family_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/12?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ASK_SWISS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ask_swiss_meta_title,
    description: seo?.ask_swiss_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/13?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const TOGETHER_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.together_meta_title,
    description: seo?.together_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/14?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const LOVE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.love_meta_title,
    description: seo?.love_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/15?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const MY_AREA_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.my_area_meta_title,
    description: seo?.my_area_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/16?city=${city}&tag_filter=most_popular&duration=30&moduleName=community&postType=16&type=posts&view=list`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const PHOTOTRIP_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.phototrip_meta_title,
    description: seo?.phototrip_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/17?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONSTER_HELP_RONSTER_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.ronster_help_ronster_meta_title,
    description: seo?.ronster_help_ronster_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/categories/18?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_HOME_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_home_meta_title,
    description: seo?.rons_tips_home_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const MARKET_HOME_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.market_home_meta_title,
    description: seo?.market_home_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_GUIDES_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_guides_meta_title,
    description: seo?.rons_tips_guides_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_meta_title,
    description: seo?.rons_tips_family_life_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_DIY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_diy_meta_title,
    description: seo?.rons_tips_family_life_diy_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/diy?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_FINANCE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_finance_meta_title,
    description: seo?.rons_tips_family_life_finance_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/finance?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_PARENTHOOD_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_parenthood_meta_title,
    description: seo?.rons_tips_family_life_parenthood_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/parenthood?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_PODCASTS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_podcasts_meta_title,
    description: seo?.rons_tips_family_life_podcasts_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/podcasts?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_TRIPS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_trips_meta_title,
    description: seo?.rons_tips_family_life_trips_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/trips?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FAMILY_LIFE_INSPIRATION_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_family_life_inspiration_meta_title,
    description: seo?.rons_tips_family_life_inspiration_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/family-life/inspiration?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_CITY_LIFE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_city_life_meta_title,
    description: seo?.rons_tips_city_life_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/city-life?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_RONS_EVENTS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_rons_events_meta_title,
    description: seo?.rons_tips_rons_events_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/rons-events?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_WHATS_HAPPENING_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_whats_happening_meta_title,
    description: seo?.rons_tips_whats_happening_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/whats-happening?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_WHATS_UP_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_whats_up_meta_title,
    description: seo?.rons_tips_whats_up_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/whats-up?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_BALANCE_DAY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_balance_day_meta_title,
    description: seo?.rons_tips_balance_day_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/balance-day?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_PASTIME_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_pastime_meta_title,
    description: seo?.rons_tips_pastime_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/pastime?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_RON_VOYAGE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_ron_voyage_meta_title,
    description: seo?.rons_tips_ron_voyage_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/ron-voyage?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_RONCAST_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_roncast_meta_title,
    description: seo?.rons_tips_roncast_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/roncast?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_ROOFTOP_DAY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_rooftop_day_meta_title,
    description: seo?.rons_tips_rooftop_day_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/rooftop-day`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_STREETFOOD_TOUR_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_streetfood_tour_meta_title,
    description: seo?.rons_tips_streetfood_tour_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/guides/streetfood-tour`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FOOD_AND_SHOP_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_food_and_shop_meta_title,
    description: seo?.rons_tips_food_and_shop_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/food-shopping?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FOOD_AND_DRINKS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_food_and_drinks_meta_title,
    description: seo?.rons_tips_food_and_drinks_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/food-shopping/food-drinks?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_NEW_IN_TOWN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_new_in_town_meta_title,
    description: seo?.rons_tips_new_in_town_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/food-shopping/new-in-town?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_SHOPPING_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_shopping_meta_title,
    description: seo?.rons_tips_shopping_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/food-shopping/shopping?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_CONTEST_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_contest_meta_title,
    description: seo?.rons_tips_contest_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/contest?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_PEOPLE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_people_meta_title,
    description: seo?.rons_tips_people_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/people?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_OUT_OF_TOWN_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_out_of_town_meta_title,
    description: seo?.rons_tips_out_of_town_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/out-of-town?city=${city}&tag_filter=most_popular`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const getMarketCategoryPageMeta = (postType, categoryId, city) => {
  const postTypeName = postType;
  if (city == 'zurich-en') {
    postType = enPostTypes[postType];
  } else if (city == 'romandie') {
    postType = frPostTypes[postType];
  } else {
    postType = dePostTypes[postType];
  }
  return {
    title: postType?.meta_title,
    description: postType?.meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/${postTypeName}/${categoryId}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const getMarketPostTypeHomePageMeta = (postType, city) => {
  const postTypeName = postType;
  if (city == 'zurich-en') {
    postType = enPostTypes[postType];
  } else if (city == 'romandie') {
    postType = frPostTypes[postType];
  } else {
    postType = dePostTypes[postType];
  }
  return {
    title: postType?.meta_title,
    description: postType?.meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/${postTypeName}?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const getCommunityCategoryPageMeta = (categoryId, city) => {
  let category;

  if (categoryId == '2') {
    category = ASK_ZURICH_EN_META(city);
  } else if (categoryId == '3') {
    category = ASK_ZURICH_META(city);
  } else if (categoryId == '4') {
    category = ASK_ROMANDIE_META(city);
  } else if (categoryId == '6') {
    category = ASK_BASEL_META(city);
  } else if (categoryId == '7') {
    category = ASK_BERN_META(city);
  } else if (categoryId == '8') {
    category = ASK_LUZERN_META(city);
  } else if (categoryId == '9') {
    category = ASK_ST_GALLEN_META(city);
  } else if (categoryId == '10') {
    category = ASK_WINTERTHUR_META(city);
  } else if (categoryId == '12') {
    category = FAMILY_ZURICH_META(city);
  } else if (categoryId == '13') {
    category = ASK_SWISS_META(city);
  } else if (categoryId == '14') {
    category = TOGETHER_META(city);
  } else if (categoryId == '15') {
    category = LOVE_META(city);
  } else if (categoryId == '16') {
    category = MY_AREA_META(city);
  } else if (categoryId == '17') {
    category = PHOTOTRIP_META(city);
  } else if (categoryId == '18') {
    category = RONSTER_HELP_RONSTER_META(city);
  } else {
    category = COMMUNITY_HOME_META(city);
  }

  return {
    title: category?.title,
    description: category?.description,
    canonical: category?.canonical,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const getCommunityPostDetailsMeta = (
  postTitle,
  postDescription,
  image,
  postId,
  city
) => {
  return {
    title: postTitle || 'Ron Community Post Details',
    description:
      getMetaDescriptionForPosts(postDescription) ||
      'Ron Community Post Details',
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/posts/${postId}?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(image),
    },
    twitter: twitterMeta,
  };
};

export const getRonsTipsPostDetailsMeta = (
  postTitle,
  postDescription,
  image,
  postId,
  city
) => {
  return {
    title: postTitle || "Ron's Tips Post Details",
    description:
      getMetaDescriptionForPosts(postDescription) || "Ron's Tips Post Details",
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/${postId}?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(image),
    },
    twitter: twitterMeta,
  };
};

export const getRonsTipsListiclePostDetailsMeta = (
  postTitle,
  postDescription,
  image,
  postId,
  city
) => {
  return {
    title: postTitle || "Ron's Tips Listicle",
    description:
      getMetaDescriptionForPosts(postDescription) || "Ron's Tips Listicle",
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/${postId}?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(image),
    },
    twitter: twitterMeta,
  };
};

export const getMarketPostDetailsMeta = (
  postTitle,
  postDescription,
  image,
  postId,
  city
) => {
  return {
    title: postTitle || 'Market Post Details',
    description:
      getMetaDescriptionForPosts(postDescription) || 'Market Post Details',
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/posts/${postId}?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(image),
    },
    twitter: twitterMeta,
  };
};

export const ABOUT_RON_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.about_ron_meta_title,
    description: seo?.about_ron_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/about-ron`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const GTC_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.terms_and_conditions_meta_title,
    description: seo?.terms_and_conditions_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/gtc`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const PRIVACY_POLICY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.privacy_policy_meta_title,
    description: seo?.privacy_policy_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/privacy-policy`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ADVERTISE_ON_RON_ORP_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.advertise_on_ron_orp_meta_title,
    description: seo?.advertise_on_ron_orp_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/advertise-on-ron-orp?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const PLACE_AD_ON_WEBSITE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.place_ad_on_website_meta_title,
    description: seo?.place_ad_on_website_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/place-ad-on-website?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const POST_IN_NEWSLETTER_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.post_in_newsletter_meta_title,
    description: seo?.post_in_newsletter_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/post-in-newsletter?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const TOP_PLACEMENT_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.top_placement_meta_title,
    description: seo?.top_placement_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/top-placement?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const START_DISCUSSION_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.start_new_discussion_meta_title,
    description: seo?.start_new_discussion_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/start-discussion`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_STORY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_story_meta_title,
    description: seo?.create_story_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/start-discussion/create-story`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_SURVEY_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_survey_meta_title,
    description: seo?.create_survey_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/start-discussion/create-survey`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_PHOTOTRIP_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_phototrip_meta_title,
    description: seo?.create_phototrip_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/start-discussion/create-phototrip`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_TOGETHER_EVENT_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_together_event_meta_title,
    description: seo?.create_together_event_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/start-discussion/create-event`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const LOVE_REGISTRATION_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.love_registration_meta_title,
    description: seo?.love_registration_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/love-registration`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const PLACE_AD_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.place_ad_meta_title,
    description: seo?.place_ad_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_EVENT_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_events_meta_title,
    description: seo?.create_phototrip_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/events`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_HOUSING_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_housing_meta_title,
    description: seo?.create_housing_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/housing`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_SECONDHAND_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_secondhand_meta_title,
    description: seo?.create_secondhand_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/secondhand`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_JOBS_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_jobs_meta_title,
    description: seo?.create_jobs_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/jobs`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_CITY_OFFER_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_city_offer_meta_title,
    description: seo?.create_city_offer_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/cityoffer`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const CREATE_COMMERCIAL_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.create_create_commercial_meta_title,
    description: seo?.create_create_commercial_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/commercial`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const ACCOUNT_SETTINGS_META = {
  title: 'Account Settings',
  description: 'Account Settings',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/account-settings`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const ADD_NEW_PROFILE_META = {
  title: 'Add New Profile',
  description: 'Add New Profile',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/add-new-profile`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const APPLY_AS_A_CITY_WRITER_META = {
  title: 'Apply as a City Writer',
  description: 'Apply as a City Writer',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/apply-as-a-city-writer`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const CHATS_META = {
  title: 'Chats',
  description: 'Chats',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/chats`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const CHECKOUT_META = {
  title: 'Checkout',
  description: 'Checkout',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/checkout`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const DASHBOARD_META = {
  title: 'Dashboard',
  description: 'Dashboard',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/dashboard`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const EDIT_PROFILE_META = {
  title: 'Edit Profile',
  description: 'Edit Profile',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/edit-profile`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const MY_FOLLOWS_META = {
  title: 'My Follows',
  description: 'My Follows',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/my-follows`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const NOTIFICATIONS_META = {
  title: 'Notifications',
  description: 'Notifications',
  canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/notifications`,
  openGraph: {
    ...defaultImagesAndTypes(),
  },
  twitter: twitterMeta,
};

export const getProfileMeta = (name) => {
  return {
    title: `Profile | ${name}`,
    description: 'Profile',
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/profile`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const getSearchResultsMeta = (module = 'community') => {
  return {
    title: `Search Results | ${capitalizeFirst(module)}`,
    description: 'Search Results',
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${module}/search`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const NEWSLETTER_ARCHIVE_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.newsletter_archive_meta_title,
    description: seo?.newsletter_archive_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/newsletter-archive?city=${city}`,
  openGraph: {
    ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const MARKET_FOR_YOU_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.market_for_you_meta_title,
    description: seo?.market_for_you_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/for-you?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const RONS_TIPS_FOR_YOU_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.rons_tips_for_you_meta_title,
    description: seo?.rons_tips_for_you_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/rons-tips/for-you?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};

export const COMMUNITY_FOR_YOU_META = (city) => {
  const seo = getSeoMeta(city);
  return {
    title: seo?.community_for_you_meta_title,
    description: seo?.community_for_you_meta_description,
    canonical: `${process.env.NEXT_PUBLIC_FRONTEND_URL}/community/for-you?city=${city}`,
    openGraph: {
      ...defaultImagesAndTypes(),
    },
    twitter: twitterMeta,
  };
};


